import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { labAPI, workerAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const LabBulkAssignmentModal = ({ isOpen, onClose, onSuccess, salesOrderId }) => {
  const { t } = useTranslation();
  const { showError, showWarning } = useNotification();
  const [workers, setWorkers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, salesOrderId]);

  const loadData = async () => {
    try {
      setTaskLoading(true);
      const [workersResponse, tasksResponse] = await Promise.all([
        workerAPI.getAll({}),
        salesOrderId
          ? labAPI.getSalesOrderTasks(salesOrderId)
          : labAPI.getAll({ status: 'Pending', include_non_fabric: true })
      ]);

      setWorkers(workersResponse.data.data || workersResponse.data || []);

      // Normalize possible response shapes (paginated or plain array)
      const tasksData = tasksResponse.data?.data?.data || tasksResponse.data?.data || tasksResponse.data || [];
      const unassignedTasks = (tasksData || []).filter(t => !t.worker_id);
      setTasks(unassignedTasks);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setTaskLoading(false);
    }
  };

  const handleTaskSelection = (taskId) => {
    setSelectedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(t => t.id));
    }
  };

  const handleAssign = async () => {
    if (!selectedWorker) {
      showWarning('Please select a worker');
      return;
    }

    if (selectedTasks.length === 0) {
      showWarning('Please select at least one task');
      return;
    }

    try {
      setLoading(true);
      const assignments = selectedTasks.map(taskId =>
        labAPI.assignWorker(taskId, { worker_id: selectedWorker })
      );

      await Promise.all(assignments);
      
      onSuccess?.();
      setSelectedWorker('');
      setSelectedTasks([]);
      onClose();
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const allTasksSelected = selectedTasks.length === tasks.length && tasks.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-gray-700 p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold text-white">Bulk Assign Tasks to Worker</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-yellow-400 text-2xl leading-none transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Worker Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              {t('lab.selectWorker')} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedWorker}
              onChange={(e) => setSelectedWorker(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white text-gray-900 font-medium"
            >
              <option value="">{t('lab.chooseWorker')}</option>
              {workers.map(worker => (
                <option key={worker.id} value={worker.id}>
                  {worker.full_name} - {worker.skills}
                </option>
              ))}
            </select>
          </div>

          {/* Tasks Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-semibold text-gray-900">
                {t('lab.selectTasks')} <span className="text-yellow-600">({selectedTasks.length}/{tasks.length})</span>
              </label>
              {tasks.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-yellow-600 hover:text-yellow-700 font-bold transition-colors"
                >
                  {allTasksSelected ? t('lab.deselectAll') : t('lab.selectAll')}
                </button>
              )}
            </div>

            {taskLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-yellow-500"></div>
              </div>
            ) : tasks.length > 0 ? (
              <div className="border-2 border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                {tasks.map(task => (
                  <label
                    key={task.id}
                    className="flex items-center p-4 hover:bg-yellow-50 cursor-pointer transition-colors border-l-4 border-l-transparent hover:border-l-yellow-500"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.id)}
                      onChange={() => handleTaskSelection(task.id)}
                      className="h-5 w-5 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-bold text-gray-900">{task.lab_number}</p>
                      <p className="text-sm text-gray-600">SO-{task.sales_order_id} • Status: <span className="font-semibold text-gray-800">{task.status}</span></p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-100 rounded-lg border-2 border-gray-200 border-dashed">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-600 font-medium">No unassigned tasks available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-200 p-6 flex gap-4 bg-gray-50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || !selectedWorker || selectedTasks.length === 0}
            className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:text-gray-600 transition-colors font-bold"
          >
            {loading ? t('lab.assigningTasks') : `${t('common.assign')} ${selectedTasks.length} ${t('common.tasks')}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabBulkAssignmentModal;
